import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { SectionHeading, TextField, ToggleRow } from 'src/components/app-ui'
import UserType from 'src/model/user-types'
import Badge from 'src/components/user-access/components/badge'
import UpgradePopover from 'src/components/upgrade-popover/upgrade-popover'
import usePathSettings, { useSettingField } from '../hooks/usePathSettings'

// Plans that include the accessibility widget.
const ACCESSIBILITY_PLANS = [
  'Elite',
  'AppSumo (Tier 5)',
  'AppSumo (Tier 6)',
  'AppSumo (Tier 7)',
  'AppSumo (Tier 8)',
  'AppSumo (Tier 9)',
  'Super Admin',
  'Plus',
  'Pro v2',
  'Launch',
  'Essentials',
  'Growth',
  'Scale',
  'Enterprise',
]

const hasAccessibilityAccess = () => {
  const plan = UserType[window.CONSTANTS.USER_ROLE_ID]
  return ACCESSIBILITY_PLANS.includes(plan) || UserType.isTrialEquivalent(plan)
}

const useStyles = makeStyles({
  title: {
    maxWidth: 640,
    fontSize: '14px',
    color: '#002E47',
  },
  toggles: {
    display: 'flex',
    flexDirection: 'column',
    gap: '22px',
    marginTop: 4,
  },
  badge: {
    display: 'inline-flex',
    marginLeft: 8,
    verticalAlign: 'middle',
  },
})

const GeneralSection = () => {
  const classes = useStyles()
  const { collection, patchCollection } = usePathSettings()
  const [title, setTitle] = useState(collection.name)
  const [hideRelated, setHideRelated] = useSettingField('hideRelatedCourses')
  const [a11y, setA11y] = useSettingField('enableAccessibilityWidget')
  const [upgradeAnchor, setUpgradeAnchor] = useState(null)

  useEffect(() => {
    setTitle(collection.name)
  }, [collection.name])

  const saveTitle = () => {
    const next = title.trim()
    if (next && next !== collection.name)
      patchCollection('collectionName', next)
  }

  const handleA11y = (value, event) => {
    if (!hasAccessibilityAccess()) {
      setUpgradeAnchor(event.currentTarget)
      return
    }
    setA11y(value)
  }

  return (
    <>
      <SectionHeading
        title="General settings"
        description="Customize settings for your learning path to meet specific requirements and optimize the learner experience."
      />
      <TextField
        label="Path title"
        className={classes.title}
        value={title}
        onChange={event => setTitle(event.target.value)}
        onBlur={saveTitle}
        onKeyDown={event => event.key === 'Enter' && event.target.blur()}
      />
      <div className={classes.toggles}>
        <ToggleRow
          title="Hide related courses"
          description="Learners don't see other courses on the completion page."
          checked={Boolean(hideRelated)}
          onChange={setHideRelated}
        />
        <ToggleRow
          title="Show accessibility widget"
          description="Show a floating accessibility menu to assist learners with disabilities."
          checked={Boolean(a11y)}
          onChange={handleA11y}
          extra={
            <span className={classes.badge}>
              <Badge type={6} />
            </span>
          }
        />
      </div>
      <UpgradePopover
        id="accessibility-popover"
        open={Boolean(upgradeAnchor)}
        anchorEl={upgradeAnchor}
        handleClose={() => setUpgradeAnchor(null)}
        header="Upgrade to Plus"
        content="Upgrade to Plus plan to access the accessibility widget."
        accessRuleEqual={21}
      />
    </>
  )
}

export default GeneralSection
